// Typed wrappers for the /v1/platform/payouts surface (Phase 8).
//
// Admin-only. amount_minor crosses the wire as a JSON STRING (bigint-safe past
// JS 2^53) — same pattern as merchant-side payouts. Normalised to bigint at
// the deserialization boundary so UI consumers can never accidentally float-
// coerce (CLAUDE.md rule 6).
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient.
import { apiClient } from "./client";

// ---------------------------------------------------------------------------
// Buckzy party shape — required ONLY when the platform rail is Buckzy AND
// no cached platform_beneficiary exists for (rail, target). The handler
// returns 400 buckzy_party_required if missing on a Buckzy first-send.
// ---------------------------------------------------------------------------

export interface BuckzyContactDetail {
  mobile_country_code?: string;
  mobile_number?: string;
  email_address?: string;
}

export interface BuckzyPostalAddress {
  address_line1: string;
  address_line2?: string;
  address_line3?: string;
  city: string;
  province?: string;
  country: string;
  postal_code?: string;
}

export interface BuckzyPartyPayload {
  party_type: "B" | "I";
  external_id?: string;
  // Business:
  legal_name?: string;
  country_of_registration?: string;
  establishment_date?: string;
  registration_number?: string;
  country_of_operation?: string;
  organization_type?: string;
  line_of_business?: string;
  tax_id?: string;
  // Individual:
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  birth_date?: string;
  country_of_birth?: string;
  country_of_residence?: string;
  country_of_citizenship?: string;
  occupation?: string;
  job_description?: string;
  // Common:
  contact_detail?: BuckzyContactDetail;
  postal_address: BuckzyPostalAddress;
  clearing_channel: "IMPS" | "NEFT" | "UPI";
  account_type?: string;
}

// ---------------------------------------------------------------------------
// Request / response shapes
// ---------------------------------------------------------------------------

export interface InitiatePlatformPayoutPayload {
  platform_bank_account_id: number;
  target_bank_account_id: number;
  beneficiary_name: string;
  beneficiary_email?: string;
  beneficiary_phone?: string;
  amount_minor: bigint; // INR paise; serialised as JSON integer via json-bigint
  txn_mode: 1 | 2; // 1=IMPS, 2=NEFT
  request_id: string;
  buckzy_party?: BuckzyPartyPayload;
}

interface RawPlatformPayout {
  platform_payout_id: number;
  platform_bank_account_id: number;
  target_merchant_id: number;
  target_bank_account_id: number;
  /** amount_minor crosses the wire as STRING. */
  amount_minor: string;
  currency: string;
  txn_mode: number;
  request_id: string;
  status: "pending" | "success" | "failure";
  provider: string;
  provider_txn_id: string | null;
  utr_no: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
}

export interface PlatformPayout {
  platform_payout_id: number;
  platform_bank_account_id: number;
  target_merchant_id: number;
  target_bank_account_id: number;
  /** Normalised to bigint at the boundary (CLAUDE.md rule 6). */
  amount_minor: bigint;
  currency: string;
  txn_mode: number;
  request_id: string;
  status: "pending" | "success" | "failure";
  provider: string;
  provider_txn_id: string | null;
  utr_no: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
}

function toPlatformPayout(r: RawPlatformPayout): PlatformPayout {
  return {
    ...r,
    amount_minor: BigInt(r.amount_minor),
  };
}

export interface InitiatePlatformPayoutResult {
  platform_payout: PlatformPayout;
  /** true when the request was an idempotent replay (existing row returned). */
  replayed: boolean;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

/**
 * initiatePlatformPayout — POST /v1/platform/payouts.
 *
 * 201 on fresh insert; 200 on idempotent replay (same payload via the
 * request_id key). Buckzy-routed first-sends require buckzy_party — otherwise
 * the backend returns 400 buckzy_party_required.
 */
export async function initiatePlatformPayout(
  payload: InitiatePlatformPayoutPayload,
): Promise<InitiatePlatformPayoutResult> {
  const resp = await apiClient.post<{
    platform_payout: RawPlatformPayout;
    replayed: boolean;
  }>(`/v1/platform/payouts`, payload);
  return {
    platform_payout: toPlatformPayout(resp.data.platform_payout),
    replayed: resp.data.replayed,
  };
}

/**
 * listPlatformPayouts — GET /v1/platform/payouts. Newest-first, no pagination.
 */
export async function listPlatformPayouts(): Promise<PlatformPayout[]> {
  const resp = await apiClient.get<{ platform_payouts: RawPlatformPayout[] }>(
    `/v1/platform/payouts`,
  );
  return resp.data.platform_payouts.map(toPlatformPayout);
}
